import { Offer } from 'common/contracts';
import {
  MIN_USD_PRICE,
  PRICE_VALUE_LIMIT,
  TRIMMED_INPUT_REGEX
} from 'constants/constants';
import * as yup from 'yup';

type UniquenessChecker = (
  value: string | undefined,
  field: keyof Offer
) => boolean;

const buildNameSchema = (
  isUnique: UniquenessChecker,
  withTransform: boolean
): yup.StringSchema => {
  let schema = yup.string();
  if (withTransform) {
    schema = schema.transform((value: string) => value.trim());
  }
  return schema
    .matches(TRIMMED_INPUT_REGEX, 'Name should not be spaces')
    .min(3, 'Name length should be longer')
    .required('Name is required')
    .test('uniqueness', 'Name already exists', (value) => {
      return isUnique(value, 'name');
    });
};

export const nameFieldSchema = (
  isUnique: UniquenessChecker
): yup.StringSchema => buildNameSchema(isUnique, false);

export const nameFieldWithTransformSchema = (
  isUnique: UniquenessChecker
): yup.StringSchema => buildNameSchema(isUnique, true);

const buildPublisherOfferIdSchema = (
  isUnique: UniquenessChecker,
  withTransform: boolean
): yup.StringSchema => {
  let schema = yup.string();
  if (withTransform) {
    schema = schema.transform((value: string) => value.trim());
  }
  return schema
    .matches(TRIMMED_INPUT_REGEX, 'External ID should not be spaces')
    .required('External ID is required')
    .test('uniqueness', 'External ID already exists', (value) => {
      return isUnique(value, 'publisherOfferId');
    });
};

export const publisherOfferIdFieldSchema = (
  isUnique: UniquenessChecker
): yup.StringSchema => buildPublisherOfferIdSchema(isUnique, false);

export const publisherOfferIdWithTransformFieldSchema = (
  isUnique: UniquenessChecker
): yup.StringSchema => buildPublisherOfferIdSchema(isUnique, true);

export const offerUiIdFieldSchema = (): yup.StringSchema =>
  yup.string().required('Offer Design is required');

export const descriptionFieldSchema = (): yup.StringSchema =>
  yup
    .string()
    .nullable()
    .test(
      'is-empty-or-min-length',
      'Description length should be longer',
      (value) => !value || value.length >= 3
    ) as yup.StringSchema;

export const standardPriceFieldSchema = (): yup.NumberSchema =>
  yup
    .number()
    .min(MIN_USD_PRICE, 'Price must be at least 0.8')
    .max(5000, 'Price must be at most 5000')
    .required('Price is required')
    .test('maxDigits', 'Price must have at most 10 digits', (value) =>
      value ? value.toString().length <= PRICE_VALUE_LIMIT : true
    );

export const freePriceFieldSchema = (): yup.NumberSchema =>
  yup.number().when('setAsFree', {
    is: true,
    then: yup
      .number()
      .oneOf([0], 'Price must be 0 if the offer is set as free'),
    otherwise: standardPriceFieldSchema()
  }) as yup.NumberSchema;

export const rollingOfferPriceFieldSchema = (): yup.NumberSchema =>
  yup.number().when('setAsFree', {
    is: true,
    then: yup.number(),
    otherwise: yup
      .number()
      .required('price is required')
      .min(MIN_USD_PRICE, 'Price must be at least 0.8')
      .max(5000, 'Price must be at most 5000')
      .test('maxDigits', 'Price must have at most 10 digits', (value) =>
        value
          ? value.toString().replace('.', '').length <= PRICE_VALUE_LIMIT
          : true
      )
      .test(
        'maxDecimalPlaces',
        'Price must have at most 2 decimal points',
        (value) => {
          if (!value) {
            return true;
          }
          const decimalPart = value.toString().split('.')[1];
          return !decimalPart || decimalPart.length <= 2;
        }
      )
      .test(
        'maxIntegerPart',
        'Price integer part must have at most 8 digits',
        (value) => {
          if (!value) {
            return true;
          }
          const integerPart = value.toString().split('.')[0];
          return integerPart.length <= 8;
        }
      )
  }) as yup.NumberSchema;

export const discountFieldSchema = (): yup.NumberSchema =>
  yup
    .number()
    .min(0, 'Price discount cannot be negative')
    .max(99, 'Price discount cannot be more than 99%')
    .test(
      'is-one-decimal',
      'Price discount can have one decimal place at most',
      (value) =>
        value === undefined || /^\d+(\.\d{1})?$/.test(value.toString())
    );

export const salePercentageFieldSchema = (): yup.NumberSchema =>
  yup
    .number()
    .min(0, 'Sale amount cannot be negative')
    .test(
      'is-one-decimal',
      'Sale amount can have one decimal place at most',
      (value) =>
        value === undefined || /^\d+(\.\d{1})?$/.test(value.toString())
    );

export const productSaleFieldSchema = (): yup.NumberSchema =>
  yup.number().min(0, 'Sale amount cannot be negative');

export const priceDiscountFieldSchema = (): yup.NumberSchema =>
  yup
    .number()
    .min(0, 'Price discount cannot be negative')
    .max(99, 'Price discount cannot be more than 99%');

export const segmentsFieldSchema = (): yup.ArraySchema<yup.StringSchema> =>
  yup.array().of(yup.string());

export const tagsFieldSchema = (): yup.ArraySchema<yup.StringSchema> =>
  yup.array().of(yup.string());

export const setAsFreeFieldSchema = (): yup.BooleanSchema =>
  yup.boolean();

export const priorityFieldSchema = (): yup.NumberSchema =>
  yup
    .number()
    .min(1, 'Priority must be at least 1')
    .max(10, 'Priority must be at most 10')
    .required('Priority is required');
